// Month calendar: items placed on their due date; drag to another day to reschedule.
import {
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Item, UpdateItem } from "../api/types";
import { Icon } from "../components/icons";
import { useItemMenu } from "../components/ItemMenu";
import {
  formatTime,
  formatYmdWith,
  isClosed,
  isItemOverdue,
  itemYmd,
  moveIsoToDay,
  shiftIsoDays,
  startHasTime,
} from "../lib/format";
import { addDaysYmd, diffDaysYmd, isoWeek, todayYmd, weekdayMon0 } from "../lib/tz";
import { patchItem, toastError } from "../store/app";
import { useApp } from "../store/hooks";

const MAX_PER_DAY = 3;
/** Drop where the pointer is; keyboard drags (no pointer) fall back to rectangle overlap. */
const collision: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return hits.length ? hits : rectIntersection(args);
};
const NODATE = "nodate";

function monthStart(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`;
}
function addMonths(ymd: string, n: number): string {
  const [y, m] = ymd.split("-").map(Number);
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}-01`;
}
function daysInMonth(ymd: string): number {
  const [y, m] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

const WEEKDAY_LABELS = Array.from({ length: 7 }, (_, i) =>
  // 2024-01-01 was a Monday.
  formatYmdWith(addDaysYmd("2024-01-01", i), { weekday: "short" }),
);

/** Reschedule an item to `ymd` (null = clear), preserving time and the start→due duration. */
export function rescheduleToDay(item: Item, ymd: string | null): void {
  let patch: UpdateItem;
  if (ymd === null) {
    patch = { due_at: null, due_has_time: false };
  } else {
    patch = { due_at: moveIsoToDay(item.due_at, item.due_has_time, ymd), due_has_time: item.due_at ? item.due_has_time : false };
    if (item.due_at && item.start_at) {
      const delta = diffDaysYmd(itemYmd(item.due_at, item.due_has_time), ymd);
      if (delta) patch.start_at = shiftIsoDays(item.start_at, startHasTime(item.start_at), delta);
    }
  }
  patchItem(item.id, patch).catch((e) => toastError(e, "Could not reschedule"));
}

function CalendarChip({ item, overlay }: { item: Item; overlay?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: item.id, disabled: overlay });
  const done = isClosed(item.status_category);
  const overdue = isItemOverdue(item);
  const menu = useItemMenu(item);
  return (
    <>
    <button
      type="button"
      ref={overlay ? undefined : setNodeRef}
      className={`cal-item${done ? " done" : ""}${overdue ? " overdue" : ""}${isDragging ? " dragging" : ""}${overlay ? " overlay" : ""}`}
      title={item.title}
      {...(overlay ? {} : attributes)}
      {...(overlay ? {} : listeners)}
      onClick={() => navigate(`/items/${item.id}`, { state: { background: location } })}
      onContextMenu={overlay ? undefined : menu.targetProps.onContextMenu}
    >
      <span className={`cat-dot cat-${item.status_category}`} aria-hidden="true" />
      {item.due_has_time && item.due_at && <span className="cal-time">{formatTime(item.due_at)}</span>}
      <span className="cal-title">{item.title || "(untitled)"}</span>
    </button>
    {!overlay && menu.menu}
    </>
  );
}

function DayCell({
  ymd,
  items,
  inMonth,
  isToday,
  showWeek,
  expanded,
  onExpand,
}: {
  ymd: string;
  items: Item[];
  inMonth: boolean;
  isToday: boolean;
  showWeek: boolean;
  expanded: boolean;
  onExpand: (open: boolean) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${ymd}` });
  const shown = expanded ? items : items.slice(0, MAX_PER_DAY);
  const hidden = items.length - shown.length;
  const label = formatYmdWith(ymd, { weekday: "long", month: "long", day: "numeric" });
  return (
    <div
      ref={setNodeRef}
      role="gridcell"
      aria-label={`${label}, ${items.length} item${items.length === 1 ? "" : "s"}`}
      className={`cal-day${inMonth ? "" : " outside"}${isToday ? " today" : ""}${isOver ? " over" : ""}${expanded ? " expanded" : ""}`}
    >
      <div className="cal-day-head">
        {showWeek && <span className="cal-week" title="Week number">W{isoWeek(ymd)}</span>}
        <span className="cal-daynum">{Number(ymd.slice(8))}</span>
      </div>
      <div className="cal-items">
        {shown.map((it) => (
          <CalendarChip key={it.id} item={it} />
        ))}
        {hidden > 0 && (
          <button type="button" className="cal-more" onClick={() => onExpand(true)}>
            +{hidden} more
          </button>
        )}
        {expanded && items.length > MAX_PER_DAY && (
          <button type="button" className="cal-more" onClick={() => onExpand(false)}>
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

function NoDateList({ items }: { items: Item[] }) {
  const { setNodeRef, isOver } = useDroppable({ id: NODATE });
  return (
    <aside className={`cal-nodate${isOver ? " over" : ""}`} ref={setNodeRef} aria-label="Items without a due date">
      <h2 className="cal-side-title">
        No date <span className="count">{items.length}</span>
      </h2>
      <p className="muted small">Drag onto a day to schedule.</p>
      <div className="cal-nodate-list">
        {items.map((it) => (
          <CalendarChip key={it.id} item={it} />
        ))}
        {items.length === 0 && <p className="muted small">Everything is scheduled.</p>}
      </div>
    </aside>
  );
}

export function CalendarView({ items }: { items: Item[] }) {
  useApp((s) => s.timezone); // re-render on time zone change
  const today = todayYmd();
  const [month, setMonth] = useState(() => monthStart(today));
  const [expanded, setExpanded] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const relevant = useMemo(
    () => items.filter((i) => i.type !== "meeting" && (showDone || !isClosed(i.status_category))),
    [items, showDone],
  );
  const byDay = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const it of relevant) {
      if (!it.due_at) continue;
      const k = itemYmd(it.due_at, it.due_has_time);
      const list = m.get(k) ?? [];
      list.push(it);
      m.set(k, list);
    }
    for (const list of m.values()) {
      list.sort((a, b) => {
        if (a.due_has_time !== b.due_has_time) return a.due_has_time ? -1 : 1;
        return (a.due_at ?? "").localeCompare(b.due_at ?? "") || b.priority - a.priority;
      });
    }
    return m;
  }, [relevant]);
  const noDate = useMemo(() => relevant.filter((i) => !i.due_at && !i.parent_id), [relevant]);
  const itemMap = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const gridStart = addDaysYmd(month, -weekdayMon0(month));
  const weeks = Math.ceil((weekdayMon0(month) + daysInMonth(month)) / 7);
  const days = Array.from({ length: weeks * 7 }, (_, i) => addDaysYmd(gridStart, i));
  const monthLabel = formatYmdWith(month, { month: "long", year: "numeric" });

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const item = itemMap.get(String(e.active.id));
    const over = e.over ? String(e.over.id) : null;
    if (!item || !over) return;
    if (over === NODATE) {
      if (item.due_at) rescheduleToDay(item, null);
      return;
    }
    const ymd = over.slice(4);
    if (item.due_at && itemYmd(item.due_at, item.due_has_time) === ymd) return;
    rescheduleToDay(item, ymd);
  };
  const activeItem = activeId ? itemMap.get(activeId) : undefined;

  return (
    <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
      <div className="cal-toolbar">
        <div className="cal-nav">
          <button type="button" className="icon-btn" aria-label="Previous month" onClick={() => setMonth((m) => addMonths(m, -1))}>
            <Icon name="chevronLeft" />
          </button>
          <button type="button" className="btn small" onClick={() => setMonth(monthStart(today))}>
            Today
          </button>
          <button type="button" className="icon-btn" aria-label="Next month" onClick={() => setMonth((m) => addMonths(m, 1))}>
            <Icon name="chevronRight" />
          </button>
        </div>
        <h2 className="cal-month" aria-live="polite">
          {monthLabel}
        </h2>
        <label className="inline-field small muted">
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
          Show done
        </label>
      </div>
      <div className="cal-layout">
        <div className="cal-grid" role="grid" aria-label={monthLabel}>
          <div className="cal-weekdays" role="row">
            {WEEKDAY_LABELS.map((w) => (
              <div key={w} role="columnheader" className="cal-weekday">
                {w}
              </div>
            ))}
          </div>
          {Array.from({ length: weeks }, (_, w) => (
            <div className="cal-week-row" role="row" key={w}>
              {days.slice(w * 7, w * 7 + 7).map((d, i) => (
                <DayCell
                  key={d}
                  ymd={d}
                  items={byDay.get(d) ?? []}
                  inMonth={d.slice(0, 7) === month.slice(0, 7)}
                  isToday={d === today}
                  showWeek={i === 0}
                  expanded={expanded === d}
                  onExpand={(open) => setExpanded(open ? d : null)}
                />
              ))}
            </div>
          ))}
        </div>
        <NoDateList items={noDate} />
      </div>
      <DragOverlay>{activeItem ? <CalendarChip item={activeItem} overlay /> : null}</DragOverlay>
    </DndContext>
  );
}
